import React, { useState, useEffect, useCallback } from "react"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import useCartAPI from "./actions"
import { getAssetPath, debounce } from "@commonUtils"
import Button from "@components/Button/Button"
import TextualButton from "@components/TextualButton/TextualButton"
import QuantitySelector from "@components/QuantitySelector/QuantitySelector"

function AddToCart(props) {
    const {
        buttonType,
        label,
        buttonIconUrl,
        isDisabled,
        payload,
        minQuantity,
        maxQuantity,
        onAddSuccessCallback,
        onAddErrorCallback,
        onDeleteSuccessCallback,
        onDeleteErrorCallback,
        onUpdateSuccessCallback,
        onUpdateErrorCallback,
        responseType,
        types,
    } = props
    const [skuId, setSkuId] = useState(null)

    const { cartState, initiateCartAction } = useCartAPI()

    const { itemsInCart, itemsInDoneInOneCart, itemsInLabsCart, cartType, cartId, labsCartId } = useSelector(
        (state) => state.shellReducer
    )

    const currentCity = useSelector((state) => state.locationsReducer.currentCity)

    const { isAdding, isUpdating } = cartState

    //  buttonType === "secondary" is Outlined Button
    //  buttonType === "tertiary" is textOnly Button

    const icon = isAdding
        ? !(buttonType === "tertiary")
            ? getAssetPath(
                  `${
                      buttonType === "secondary"
                          ? "svgs/icons/loading-wheel_24px_action.svg"
                          : "svgs/icons/loading-wheel_24px_background.svg"
                  }`
              )
            : buttonIconUrl
        : buttonIconUrl

    const selectedLabsSKU = Object.keys(itemsInLabsCart)
    const isSelected = selectedLabsSKU.includes(skuId)

    let text

    if (isAdding) {
        if (buttonType === "tertiary") text = "Processing"
        else text = ""
    } else if (cartType === "labs") {
        if (isSelected) text = "Added"
        else text = label
    } else {
        text = label
    }

    const finalPayload = {
        ...payload,
        city: currentCity,
        response_type: responseType,
        types: types,
    }
    if (cartType !== "labs") {
        finalPayload[cartId] = cartId
    } else {
        finalPayload[labsCartId] = labsCartId
    }

    useEffect(() => {
        if (cartType !== "labs") {
            const sku_id = payload.skus?.[0]?.sku_id
            setSkuId(sku_id)
        } else {
            const sku_id = payload.skus?.[0]?.test_id
            setSkuId(sku_id)
        }
    }, [payload])

    const onQuantityChange = (count) => {
        const newPayload = { ...finalPayload, quantity: count }
        initiateCartAction("update_cart_item", newPayload, onUpdateSuccessCallback, onUpdateErrorCallback)
    }

    const debouncedOnQuantityChange = useCallback(debounce(onQuantityChange, 500), [])

    const onQuantityDelete = () => {
        const payload = {
            cart_id: cartId,
            sku_ids: [skuId],
            response_type: responseType,
            types: types,
        }
        initiateCartAction("delete_cart_item", payload, onDeleteSuccessCallback, onDeleteErrorCallback)
    }

    const labsCtaHandler = () => {
        const payload = {
            cart_id: labsCartId,
            sku_ids: [{ test_id: skuId }],
            response_type: responseType,
            types: cartType,
        }

        if (isSelected) {
            initiateCartAction(
                "delete_labs_cart_item",
                payload,
                onDeleteSuccessCallback,
                onDeleteErrorCallback
            )
        } else {
            initiateCartAction("add_labs_cart_item", payload, onAddSuccessCallback, onAddErrorCallback)
        }
    }

    if (cartType !== "labs") {
        if (typeof itemsInCart[skuId] === "object" || typeof itemsInDoneInOneCart[skuId] === "object") {
            return (
                <QuantitySelector
                    value={
                        types === "done_in_one"
                            ? itemsInDoneInOneCart[skuId]?.quantity
                            : itemsInCart?.[skuId]?.quantity
                    }
                    onChange={debouncedOnQuantityChange}
                    minRange={minQuantity}
                    maxRange={maxQuantity}
                    onDelete={() => onQuantityDelete()}
                    isDeleting={isUpdating}
                    isUpdating={isUpdating}
                />
            )
        } else
            return (
                <React.Fragment>
                    {buttonType === "textual" ? (
                        <TextualButton
                            label={text}
                            iconUrl={icon}
                            isDisabled={isDisabled}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                initiateCartAction(
                                    "add_cart_item",
                                    finalPayload,
                                    onAddSuccessCallback,
                                    onAddErrorCallback
                                )
                            }}
                        />
                    ) : (
                        <Button
                            label={text}
                            type={buttonType}
                            iconUrl={icon}
                            isDisabled={isDisabled}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                initiateCartAction(
                                    "add_cart_item",
                                    finalPayload,
                                    onAddSuccessCallback,
                                    onAddErrorCallback
                                )
                            }}
                        />
                    )}
                </React.Fragment>
            )
    } else {
        return (
            <Button
                label={text}
                type={buttonType}
                iconUrl={icon}
                isDisabled={isDisabled}
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    labsCtaHandler()
                }}
            />
        )
    }
}

AddToCart.propTypes = {
    buttonType: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
    buttonIconUrl: PropTypes.string,
    label: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    payload: PropTypes.shape({
        skus: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.shape({
                    sku_id: PropTypes.string.isRequired,
                    quantity: PropTypes.number.isRequired,
                }),
                PropTypes.shape({
                    test_id: PropTypes.string.isRequired, // for labs
                }),
            ]).isRequired
        ),
        is_recommendation_required: PropTypes.bool,
        page_source: PropTypes.string,
    }),
    responseType: PropTypes.oneOf(["brief", "full"]),
    types: PropTypes.oneOf(["pharmacy", "done_in_one", "labs"]),
    minQuantity: PropTypes.number,
    maxQuantity: PropTypes.number,
    onAddSuccessCallback: PropTypes.func,
    onAddErrorCallback: PropTypes.func,
    onDeleteSuccessCallback: PropTypes.func,
    onDeleteErrorCallback: PropTypes.func,
    onUpdateSuccessCallback: PropTypes.func,
    onUpdateErrorCallback: PropTypes.func,
}

AddToCart.defaultProps = {
    buttonType: "primary",
    responseType: "brief",
    types: "pharmacy",
}
export default AddToCart
